/**
 * Generates enhanced intake options including all years from 2011 to future years
 * @param {Array} existingIntakes - The existing intakes from the API response
 * @param {Object} config - Configuration options
 * @param {number} config.startYear - The starting year (default: 2011)
 * @param {number} config.futureYears - Number of future years to include (default: 2)
 * @returns {Array} - Enhanced intake options with all years and intakes 1-4
 */
export const generateIntakeOptions = (existingIntakes = [], config = {}) => {
  const startYear = config.startYear ?? 2011;
  const futureYears = config.futureYears ?? 2;

  const currentYear = new Date().getFullYear();
  const intakeMap = new Map();

  // Load existing options from API
  existingIntakes.forEach((item) => {
    if (!item?.value || typeof item.value !== "string") return;

    // Handle both formats: "2026/1" and "2026-1"
    const normalized = item.value.replace("/", "-");
    const [yearStr, numberStr] = normalized.split("-");
    const yearNum = Number(yearStr);
    const intakeNum = Number(numberStr);

    if (!Number.isFinite(yearNum) || !Number.isFinite(intakeNum) || yearNum < startYear) return;

    if (!intakeMap.has(yearNum)) intakeMap.set(yearNum, new Set());
    intakeMap.get(yearNum).add(intakeNum);
  });

  // Fill all years and ensure intakes 1..4 exist
  for (let year = startYear; year <= currentYear + futureYears; year += 1) {
    if (!intakeMap.has(year)) intakeMap.set(year, new Set());
    [1, 2, 3, 4].forEach((n) => intakeMap.get(year).add(n));
  }

  // Build options (API uses slash, UI shows hyphen)
  const options = [];
  intakeMap.forEach((numbers, year) => {
    Array.from(numbers)
      .sort((a, b) => b - a)
      .forEach((num) => {
        options.push({
          value: `${year}/${num}`,  // API expects forward slash
          label: `${year}-${num}`,   // UI displays hyphen
        });
      });
  });

  // Sort newest -> oldest (year desc, intake desc)
  return options.sort((a, b) => {
    const [yearA, numA] = a.value.split("/").map(Number);
    const [yearB, numB] = b.value.split("/").map(Number);

    if (yearA !== yearB) return yearB - yearA;
    return numB - numA;
  });
};

/**
 * Utility function to enhance course dropdown with generated intake options
 * @param {Object} dropdownData - The dropdown data from the API
 * @returns {Object} - Enhanced dropdown data with generated intake options
 */
export const enhanceCourseDropdownWithIntakes = (dropdownData) => {
  if (!dropdownData) return {};
  
  return {
    ...dropdownData,
    programme: dropdownData.programme || [],
    intake: generateIntakeOptions(dropdownData.yearsAndIntaks || []),
    learning: dropdownData.intakeType || [],
    school: dropdownData.schoolType ? dropdownData.schoolType : [],
    type: dropdownData.subjectTypeFilter || [],
    subjectItemsArray: dropdownData.subjectItemsFilter ? dropdownData.subjectItemsFilter : []
  };
};